// Cross-tab wiring for the vault LOCK (ADR 0009, "Session shape").
//
// THE RULE THIS MODULE IS BUILT ON: the channel may take capability
// AWAY and may never hand it out. Lock travels; unlock, key handles and
// liveness do not.
//
// The asymmetry is the whole design, and it follows from what
// `BroadcastChannel` actually is: same-origin and unauthenticated. Any
// script running on this origin — an XSS, a compromised dependency, a
// stray extension content script — can post here and can listen here,
// and nothing in the message says who sent it. So ask, of every message
// type, "what does the worst sender get out of it?":
//
//   - A forged `lock` costs the operator one unlock gesture. That is an
//     annoyance, and it is the SAFE direction to be wrong in. Honour it
//     from anyone.
//   - A forged request for the key used to be answered with the live
//     `CryptoKey` handle (the old `whoHasTheKey` round trip). The bytes
//     were never clonable, but the handle is a usable key: an attacker
//     who asked politely could decrypt every vault value for as long as
//     any tab stayed unlocked, with no user gesture anywhere. That is
//     most of what non-extractability was bought for, given away for the
//     cost of one `postMessage`. Removed entirely — there is no message
//     on this channel that carries a key, and no handler that would
//     adopt one.
//   - A forged activity ping used to slide the idle deadline. A script
//     posting one a minute holds the session open forever. Removed too:
//     activity is a property of THIS tab using the key, which is a thing
//     this tab observes directly and never needs to be told.
//
// What this costs, honestly: almost nothing, now that the key lives in
// IndexedDB (ADR 0009). A second tab opened into an unlocked browser
// starts locked and then loads the stored handle itself, which is free
// and needs no gesture. It is unlocked because this browser holds the
// key — not because something on the page asked nicely. Where no key is
// stored, the cost is one recovery-file paste in the new tab, which is
// the correct price for a tab that has no other claim to it.
//
// Everything here is lazy. The module must be importable during SSR,
// where `BroadcastChannel` does not exist; nothing touches it until
// `connectUnlockChannel` runs, and that happens on a client mount.

/** Every message this channel carries. Deliberately one message with no
 * payload: see the module docblock for why nothing may be added here
 * that grants a receiving tab anything. */
interface ChannelMessage {
  type: 'lock'
}

/** Narrows an arriving `MessageEvent`'s `data`, which is `unknown`
 * because it came off the wire — a sender is not obliged to be us, or to
 * be this version of us. Anything that is not the one message this
 * channel knows about is dropped rather than guessed at. */
function isLockMessage(data: unknown): data is ChannelMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'lock'
  )
}

export interface UnlockChannelHandlers {
  onRemoteLock: () => void
}

const CHANNEL_NAME = 'stoaria.vault-unlock'

let channel: BroadcastChannel | null = null

function post(message: ChannelMessage): void {
  if (channel === null) return
  try {
    channel.postMessage(message)
  } catch (err) {
    // A browser that refuses to post must not take the local lock down
    // with it: this tab's own state is already correct, and all that is
    // lost is telling the siblings. Reported rather than swallowed —
    // `err` is a DOMException about the channel and carries no key
    // material (there is none on this channel to carry).
    console.warn('vault lock could not be shared across tabs', err)
  }
}

/** Attaches this tab to the cross-tab channel. Idempotent, and safe to
 * call from a React subscribe function: the first client-side subscriber
 * is what brings the channel up, which is also what keeps this module SSR
 * safe (nothing touches `BroadcastChannel` at import time).
 *
 * Deliberately never torn down. The channel is one object per tab with no
 * ongoing cost, and closing it when the last subscriber unmounts would
 * silently stop propagating locks to a tab that is merely between
 * renders. */
export function connectUnlockChannel(handlers: UnlockChannelHandlers): void {
  if (channel !== null) return
  if (typeof BroadcastChannel === 'undefined') return
  channel = new BroadcastChannel(CHANNEL_NAME)
  // Node's `BroadcastChannel` — which is what the jsdom test environment
  // supplies — keeps the event loop alive until it is closed, so a test
  // file that locks would hang the worker. The browser's has no `unref`
  // at all, hence the probe rather than a call. Documented cast: the DOM
  // lib correctly does not declare a Node-only method.
  const unref = (channel as { unref?: () => void }).unref
  if (typeof unref === 'function') unref.call(channel)
  channel.onmessage = (event: MessageEvent<unknown>) => {
    // No sender check, and none is possible — but none is needed either,
    // because `lock` is the only thing this tab will act on and
    // honouring a forged one is strictly safe.
    if (!isLockMessage(event.data)) return
    handlers.onRemoteLock()
  }
  // Note what is NOT here: no "does anyone have the key?" on connect. A
  // tab that mounts into an unlocked browser stays locked until a human
  // unlocks it at that tab.
}

export function broadcastLock(): void {
  post({ type: 'lock' })
}
